"use client";

import { useSiteHoteleiroPage } from "./hooks/use-site-hoteleiro-page";
import { useSeo } from "@/features/shared/lib/use-seo";
import {
  HeroSection,
  RecursosGridSection,
  ExperienciasSection,
  VantagensSection,
  ReservaSection,
  PorQueEscolherSection,
  IntegracoesSolucoesSection,
} from "./components";
import { EventosSection } from "@/features/home/components/eventos-section";
import { NumerosSection } from "@/features/home/components/numeros-section";
import { WebsitePortfolioCarousel } from "@/features/shared/components";
import { SoftwareProductsCarousel, TrustedLogosMarquee, WallOfLoveSection, DorParallaxSection } from "@/features/shared/components";
import { FAQAccordion } from "@/features/ui/components/faq-accordion";
import { LeadCaptureCTA } from "@/features/ui/components/lead-capture-cta";
import { useLeadCapture } from "@/features/shared/lib/lead-capture-context";
import { UltimasDoBlogSection } from "@/features/shared/components/ultimas-do-blog-section-lazy";
import { getProdutoIcone } from "@/features/shared/data/produtos-data";
import { Spinner } from "@/components/ui/spinner";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyContent } from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { eventos, depoimentos, numeros, videosData } from "@/features/shared/data/social-proof-data";

const LEAD_TITLE = "Site Hoteleiro";

function SiteHoteleiroPage() {
  useSeo({
    title: "Site Hoteleiro que Converte Visitantes em Reservas",
    description:
      "Site moderno, responsivo e otimizado para SEO, 100% editável. Reduza a dependência de OTAs e aumente sua margem com reservas diretas.",
    path: "/sites-para-hoteis-e-pousadas",
  });

  const { openLeadCapture } = useLeadCapture();
  const { data, isLoading, error, refetch } = useSiteHoteleiroPage();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="w-12 h-12" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Empty className="text-center">
          <EmptyHeader>
            <EmptyTitle>Erro ao carregar dados</EmptyTitle>
            <EmptyDescription>
              Não foi possível carregar o conteúdo da página. Tente novamente.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button onClick={() => refetch()} variant="outline" className="rounded-full px-6">
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar novamente
            </Button>
          </EmptyContent>
        </Empty>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Hero Section */}
      <HeroSection
        data={data.hero}
        onCtaClick={() => openLeadCapture({ source: "form-sites-para-hoteis-e-pousadas", title: LEAD_TITLE, icon: getProdutoIcone("/sites-para-hoteis-e-pousadas") })}
      />

      {/* Section 1: Modelos de sites */}
      <WebsitePortfolioCarousel />

      <TrustedLogosMarquee />

      {/* Seção 2: Por que escolher a Foco */}
      <PorQueEscolherSection />

      {/* Seção 3: Experiências */}
      <ExperienciasSection />

      {/* Seção 4: Recursos */}
      <RecursosGridSection />

      {/* Diagnóstico: baixa ocupação (mesmo cenário da home) */}
      <DorParallaxSection
        dorId="baixa-ocupacao"
        backgroundImage="/assets/imgs/motor-de-reservas/dor.webp"
        backgroundAlt="Lobby de hotel vazio à noite, recepcionista sozinha aguardando hóspedes"
      />

      {/* Seção 5: Reserva */}
      <ReservaSection />

      {/* Bloco de Autoridade e Confiança */}
      <NumerosSection numeros={numeros} />

      {/* Seção 6: Vantagens */}
      <VantagensSection />

      {/* Prova Social 1: Feiras */}
      <EventosSection eventos={eventos} />

      {/* Prova Social 2+3: Mural de depoimentos (texto + vídeo, unificados) */}
      <WallOfLoveSection
        depoimentos={depoimentos}
        videos={videosData}
        badge="Depoimentos"
        title="Um mural de resultados reais"
        subtitle="Cada card aqui é um hoteleiro de verdade, contando como a Foco aumentou suas reservas diretas."
      />

      {/* Seção 7: Integrações com soluções */}
      <IntegracoesSolucoesSection />

      {/* Prova Social: Carrossel de Produtos */}
      <SoftwareProductsCarousel />

      <UltimasDoBlogSection productSlug="sites-para-hoteis-e-pousadas" />

      {/* Prova Social 4: FAQ */}
      <FAQAccordion
        items={data.faq.map((f) => ({ id: f.id, question: f.pergunta, answer: f.resposta }))}
        title="Dúvidas Frequentes"
        subtitle="Tire suas dúvidas sobre o Site Hoteleiro"
        badge="FAQ"
        showContactButton
      />

      {/* Lead Capture CTA */}
      <LeadCaptureCTA
        title="Pronto para ter um Site Hoteleiro Profissional?"
        subtitle="Solicite uma demonstração e veja como podemos ajudar seu hotel a vender mais diretamente."
        badge="Comece agora"
        source="sites-para-hoteis-e-pousadas"
        leadTitle={LEAD_TITLE}
      />

    </div>
  );
}

export { SiteHoteleiroPage };
